// sheetPrep.js — what on a sheet is DRAWING, and what is sheet furniture (pure JS, runs in the worker).
//
//   • drawing area: the plan sits inside the sheet border, left of / above the title block. Notes columns,
//     legends, schedules and the title block are text-heavy boxes — nothing in them is a wall or a room.
//     The estimator can also draw the area(s) to trace by hand (they win over the automatic one).
//   • tag symbols: the outline a wall-type tag is drawn in (diamond, hexagon, circle, box, oval) — read from
//     closed outlines AND from loose lines around the text (CAD exports draw a diamond as 4 separate lines).
//   • column grid: grid bubbles (circle + "A" / "3" / "1.2") — the long dash-dot lines leaving them run through
//     every room and must never become a wall or cut a room.
//   • colours: pre-coloured walls / fire-rating overlays (red = 2 HR, blue = 0 HR …) and the legend that says
//     what each colour means.
//
// Units: sheet units (PDF points at scale 1, y down).

import { SYM, SYM_NAME } from "./pdfVectors.js";
import { detectScaleFromText } from "./scaleText.js";

export { SYM, SYM_NAME };
const SEG_STROKE = 1,
  SEG_FILL = 2,
  SEG_CURVE = 4;

// ───────────────────────── colour ─────────────────────────
export const rgbParts = (c) => [(c >> 16) & 255, (c >> 8) & 255, c & 255];
export const hexOf = (c) =>
  `#${(c >>> 0).toString(16).padStart(6, "0").slice(-6)}`;
/** a real colour (not black / grey / white) */
export function isColored(c) {
  const [r, g, b] = rgbParts(c);
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b);
  return mx >= 70 && mx - mn >= 60;
}
/** the colour family — so #ff0000 and #e00000 (or a legend sample and the wall it marks) match */
export function colorFamily(c) {
  const [r, g, b] = rgbParts(c).map((v) => v / 255);
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b),
    d = mx - mn;
  if (d < 0.2) return null;
  let h;
  if (mx === r) h = ((g - b) / d) % 6;
  else if (mx === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = (h * 60 + 360) % 360;
  if (h < 15 || h >= 335) return "red";
  if (h < 45) return "orange";
  if (h < 70) return "yellow";
  if (h < 165) return "green";
  if (h < 200) return "cyan";
  if (h < 255) return "blue";
  if (h < 290) return "purple";
  return "magenta";
}

// ───────────────────────── small helpers ─────────────────────────
const inRect = (x, y, r, pad = 0) =>
  x >= r[0] - pad && x <= r[2] + pad && y >= r[1] - pad && y <= r[3] + pad;
const area = (r) => Math.max(0, r[2] - r[0]) * Math.max(0, r[3] - r[1]);
function overlap(a, b) {
  return (
    Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) *
    Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]))
  );
}

// straight, axis-aligned long lines (sheet border, title-block dividers) merged along their line
export function axisLines(sheet, minFrac = 0.2) {
  const S = sheet.segs,
    F = sheet.segF,
    n = S.length >> 2;
  const W = sheet.width,
    H = sheet.height;
  const hz = new Map(),
    vt = new Map();
  for (let i = 0; i < n; i++) {
    if (F[i] & SEG_CURVE) continue;
    const x1 = S[i * 4],
      y1 = S[i * 4 + 1],
      x2 = S[i * 4 + 2],
      y2 = S[i * 4 + 3];
    const dx = Math.abs(x2 - x1),
      dy = Math.abs(y2 - y1);
    if (dy < 0.6 && dx > 4) {
      const k = Math.round((y1 + y2) / 2 / 1.5);
      if (!hz.has(k)) hz.set(k, []);
      hz.get(k).push([Math.min(x1, x2), Math.max(x1, x2), (y1 + y2) / 2]);
    } else if (dx < 0.6 && dy > 4) {
      const k = Math.round((x1 + x2) / 2 / 1.5);
      if (!vt.has(k)) vt.set(k, []);
      vt.get(k).push([Math.min(y1, y2), Math.max(y1, y2), (x1 + x2) / 2]);
    }
  }
  const merge = (m, minLen) => {
    const out = [];
    for (const arr of m.values()) {
      arr.sort((a, b) => a[0] - b[0]);
      let cur = null;
      for (const s of arr) {
        if (cur && s[0] <= cur.b + 3) {
          cur.b = Math.max(cur.b, s[1]);
          cur.o = (cur.o + s[2]) / 2;
        } else {
          if (cur && cur.b - cur.a >= minLen) out.push(cur);
          cur = { a: s[0], b: s[1], o: s[2] };
        }
      }
      if (cur && cur.b - cur.a >= minLen) out.push(cur);
    }
    return out;
  };
  return { h: merge(hz, W * minFrac), v: merge(vt, H * minFrac) };
}

// lines of text stacked into blocks (paragraphs / columns of a schedule): same column, small vertical gaps
export function textBlocks(text) {
  const T = text
    .map((t, i) => ({
      t,
      i,
      h: Math.max(1, Math.min(t.y1 - t.y0, t.x1 - t.x0, t.h || 99)),
    }))
    .sort((a, b) => a.t.y0 - b.t.y0);
  const p = T.map((_, i) => i);
  const f = (x) => {
    while (p[x] !== x) x = p[x] = p[p[x]];
    return x;
  };
  let maxH = 1;
  for (const a of T) maxH = Math.max(maxH, a.h);
  for (let i = 0; i < T.length; i++) {
    const a = T[i].t,
      ha = T[i].h;
    for (let j = i + 1; j < T.length; j++) {
      const b = T[j].t;
      if (b.y0 > a.y1 + maxH * 1.2) break;
      const h = Math.max(ha, T[j].h);
      const gapY = Math.max(a.y0, b.y0) - Math.min(a.y1, b.y1),
        ovX = Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0);
      if (gapY <= h * 0.9 && ovX > -h * 0.5) p[f(j)] = f(i);
    }
  }
  const blocks = new Map();
  T.forEach((e, k) => {
    const r = f(k),
      t = e.t;
    if (!blocks.has(r))
      blocks.set(r, {
        items: [],
        x0: Infinity,
        y0: Infinity,
        x1: -Infinity,
        y1: -Infinity,
        chars: 0,
        h: 0,
      });
    const b = blocks.get(r);
    b.items.push(e.i);
    b.x0 = Math.min(b.x0, t.x0);
    b.y0 = Math.min(b.y0, t.y0);
    b.x1 = Math.max(b.x1, t.x1);
    b.y1 = Math.max(b.y1, t.y1);
    b.chars += t.str.length;
    b.h = Math.max(b.h, e.h);
  });
  return [...blocks.values()];
}

/**
 * The part of the sheet that is drawing.
 *   opts.areas: [[x0,y0,x1,y1], …] drawn by the estimator — used as given (nothing else is cut)
 * → { rects: [[x0,y0,x1,y1]…] (drawing), excluded: [[…]…] (title block, notes, legends, schedules),
 *     frame, titleBlock, source: "manual" | "auto" | "sheet" }
 */
export function drawingArea(sheet, opts = {}) {
  const W = sheet.width,
    H = sheet.height;
  if (Array.isArray(opts.areas) && opts.areas.length) {
    const rects = opts.areas
      .filter((r) => Array.isArray(r) && r.length === 4)
      .map(([a, b, c, d]) => [
        Math.min(a, c),
        Math.min(b, d),
        Math.max(a, c),
        Math.max(b, d),
      ]);
    if (rects.length)
      return {
        rects,
        excluded: [],
        frame: [0, 0, W, H],
        titleBlock: null,
        source: "manual",
      };
  }
  if (opts.autoArea === false)
    return {
      rects: [[0, 0, W, H]],
      excluded: [],
      frame: [0, 0, W, H],
      titleBlock: null,
      source: "sheet",
    };

  const { h, v } = axisLines(sheet, 0.2);
  // sheet border: the outermost long lines near each edge
  const edge = 0.12;
  const L = v
    .filter((l) => l.o < W * edge && l.b - l.a > H * 0.6)
    .sort((a, b) => a.o - b.o)[0];
  const R = v
    .filter((l) => l.o > W * (1 - edge) && l.b - l.a > H * 0.6)
    .sort((a, b) => b.o - a.o)[0];
  const T = h
    .filter((l) => l.o < H * edge && l.b - l.a > W * 0.6)
    .sort((a, b) => a.o - b.o)[0];
  const B = h
    .filter((l) => l.o > H * (1 - edge) && l.b - l.a > W * 0.6)
    .sort((a, b) => b.o - a.o)[0];
  const frame = [L ? L.o : 0, T ? T.o : 0, R ? R.o : W, B ? B.o : H];
  const fw = frame[2] - frame[0],
    fh = frame[3] - frame[1];
  const text = sheet.text || [];
  const textIn = (r) => {
    let n = 0;
    for (const t of text)
      if (inRect((t.x0 + t.x1) / 2, (t.y0 + t.y1) / 2, r)) n++;
    return n;
  };
  let box = frame.slice();
  let titleBlock = null;
  // a title STRIP down the right side (or along the bottom): a long divider with the sheet's text past it
  {
    const cands = v
      .filter(
        (l) =>
          // (a divider runs the full height of the frame — a long wall of the building doesn't)
          l.o > frame[0] + fw * 0.7 &&
          l.o < frame[2] - fw * 0.01 &&
          l.b - l.a > fh * 0.9,
      )
      .sort((a, b) => a.o - b.o);
    for (const l of cands) {
      const r = [l.o, frame[1], frame[2], frame[3]];
      if (textIn(r) >= 8) {
        box[2] = l.o;
        titleBlock = r;
        break;
      }
    }
    if (!titleBlock) {
      const hc = h
        .filter(
          (l) =>
            l.o > frame[1] + fh * 0.75 &&
            l.o < frame[3] - fh * 0.01 &&
            l.b - l.a > fw * 0.9,
        )
        .sort((a, b) => a.o - b.o);
      for (const l of hc) {
        const r = [frame[0], l.o, frame[2], frame[3]];
        if (textIn(r) >= 8) {
          box[3] = l.o;
          titleBlock = r;
          break;
        }
      }
    }
  }
  const excluded = [];
  // a title BOX in the bottom-right corner: a horizontal line to the right border and a vertical line down
  // to the bottom border that meet
  if (!titleBlock) {
    const { h: hh, v: vv } = axisLines(sheet, 0.04);
    let best = null;
    for (const a of hh) {
      if (
        Math.abs(a.b - frame[2]) > 4 ||
        a.o < frame[1] + fh * 0.4 ||
        a.o > frame[3] - 4
      )
        continue;
      const wFrac = (a.b - a.a) / fw;
      if (wFrac < 0.1 || wFrac > 0.6) continue;
      for (const b of vv) {
        if (Math.abs(b.b - frame[3]) > 4 || Math.abs(b.o - a.a) > 4) continue;
        if (b.a > a.o + 4 || b.a < a.o - fh * 0.05) continue;
        const r = [b.o, a.o, frame[2], frame[3]];
        if (textIn(r) < 5) continue;
        if (!best || area(r) > area(best)) best = r;
      }
    }
    if (best) {
      titleBlock = best;
      excluded.push(best);
    }
  }
  // other views on the sheet: a sheet often carries details / enlarged plans at another scale in panels split
  // off by full-width / full-height divider lines. A panel whose printed scale isn't the plan's is left out.
  if (opts.ftPerUnit > 0) {
    for (const r of viewPanels(sheet, box, text, opts.ftPerUnit))
      excluded.push(r);
  }
  // notes columns, legends, schedules: text-heavy blocks (room labels are a few short lines)
  const inArea = (x, y) => inRect(x, y, box);
  for (const b of textBlocks(text)) {
    const lines = b.items.length;
    // notes are sentences (long lines); a schedule is many rows. Room labels and dimension strings are short.
    const avg = b.chars / Math.max(1, lines);
    if (
      !(
        (lines >= 5 && avg >= 12 && b.chars >= 120) ||
        (lines >= 15 && avg >= 5) ||
        (b.chars >= 400 && avg >= 10)
      )
    )
      continue;
    const r = [b.x0 - b.h, b.y0 - b.h, b.x1 + b.h, b.y1 + b.h];
    if (!inArea((r[0] + r[2]) / 2, (r[1] + r[3]) / 2)) continue;
    // a block that covers most of the sheet is a dense plan, not a notes column
    if (area(r) > fw * fh * 0.25) continue;
    excluded.push(r);
  }
  // inset so the border / divider lines themselves fall outside
  const pad = Math.min(fw, fh) * 0.004 + 1;
  box = [box[0] + pad, box[1] + pad, box[2] - pad, box[3] - pad];
  return { rects: [box], excluded, frame, titleBlock, source: "auto" };
}

// panels of the drawing area (split by divider lines running edge to edge) printed at another scale
function viewPanels(sheet, box, text, ftPerUnit) {
  const { h, v } = axisLines(sheet, 0.04);
  const bw = box[2] - box[0],
    bh = box[3] - box[1];
  const uniq = (vals, gap = 10) => {
    const out = [];
    for (const x of vals.sort((a, b) => a - b))
      if (!out.length || x - out[out.length - 1] > gap) out.push(x);
    return out;
  };
  const hy = uniq(
    h
      .filter(
        (l) =>
          l.a <= box[0] + bw * 0.08 &&
          l.b >= box[2] - bw * 0.08 &&
          l.o > box[1] + bh * 0.05 &&
          l.o < box[3] - bh * 0.05,
      )
      .map((l) => l.o),
  );
  const bands = [box[1], ...hy, box[3]];
  const cells = [];
  for (let i = 0; i + 1 < bands.length; i++) {
    const y0 = bands[i],
      y1 = bands[i + 1],
      hh = y1 - y0;
    if (hh < 20) continue;
    const vx = uniq(
      v
        .filter(
          (l) =>
            l.a <= y0 + hh * 0.08 &&
            l.b >= y1 - hh * 0.08 &&
            l.o > box[0] + bw * 0.03 &&
            l.o < box[2] - bw * 0.03,
        )
        .map((l) => l.o),
    );
    const cols = [box[0], ...vx, box[2]];
    for (let j = 0; j + 1 < cols.length; j++)
      cells.push([cols[j], y0, cols[j + 1], y1]);
  }
  // boxed views: a rectangle drawn round a detail (two horizontal lines of the same span, closed by
  // vertical lines at both ends)
  const hs = axisLines(sheet, 0.03).h,
    vs = axisLines(sheet, 0.02).v;
  // a top edge with vertical sides hanging from it: each pair of neighbouring sides closes one box
  for (const T of hs) {
    const sides = vs
      .filter(
        (l) =>
          l.o >= T.a - 4 &&
          l.o <= T.b + 4 &&
          Math.abs(l.a - T.o) < 4 &&
          l.b - l.a > bh * 0.05,
      )
      .sort((p, q) => p.o - q.o);
    for (let k = 0; k + 1 < sides.length; k++) {
      const L = sides[k],
        R = sides[k + 1];
      if (R.o - L.o < bw * 0.04) continue;
      const r = [L.o, T.o, R.o, Math.min(L.b, R.b)];
      if (r[2] - r[0] > bw * 0.7 || r[3] - r[1] > bh * 0.7) continue;
      if (!inRect((r[0] + r[2]) / 2, (r[1] + r[3]) / 2, box)) continue;
      cells.push(r);
    }
  }
  if (cells.length < 2) return [];
  const out = [];
  for (const c of cells) {
    // (a view's title + scale often sits just under its box)
    const below = [c[0], c[1], c[2], c[3] + Math.max(40, (c[3] - c[1]) * 0.15)];
    const tx = text.filter(
      (t) =>
        inRect((t.x0 + t.x1) / 2, (t.y0 + t.y1) / 2, below) ||
        inRect(t.x0 + 2, (t.y0 + t.y1) / 2, below),
    );
    // a box with no scale of its own, sitting in a row of boxes printed at another scale, is one of them
    c.unknown = !detectScaleFromText(tx);
    const sc = detectScaleFromText(tx);
    if (!sc) continue;
    c.other = true;
    const match = sc.all.some(
      (x) => Math.abs(x.ftPerUnit / ftPerUnit - 1) < 0.03,
    );
    if (!match) out.push(c);
  }
  // a box with no scale label in the same row as excluded detail boxes (same top edge) goes with them
  for (const c of cells)
    if (
      c.unknown &&
      !out.includes(c) &&
      out.some((o) => Math.abs(o[1] - c[1]) < 4 && o[3] - o[1] < bh * 0.5)
    )
      out.push(c);
  // never leave nothing: if every panel disagrees the scale is wrong, not the panels
  return out.length >= cells.length ? [] : out.map((r) => r.slice(0, 4));
}

/** test helpers for an area result */
export function areaTests(A) {
  const rects = A?.rects || [];
  const ex = A?.excluded || [];
  const inside = (x, y) =>
    (!rects.length || rects.some((r) => inRect(x, y, r))) &&
    !ex.some((r) => inRect(x, y, r));
  // share of a box that is outside the drawing (or in a notes / legend / title box)
  const outShare = (b) => {
    const a = area(b) || 1;
    let inA = 0;
    for (const r of rects.length ? rects : [[-1e9, -1e9, 1e9, 1e9]])
      inA += overlap(b, r);
    let inEx = 0;
    for (const r of ex) inEx += overlap(b, r);
    return Math.min(1, Math.max(0, 1 - inA / a) + inEx / a);
  };
  return { inside, outShare };
}

// ───────────────────────── tag symbols ─────────────────────────
/**
 * The outline each short text sits in: SYM code per text index (0 = none). Closed outlines first, then
 * loose lines that ring the text (each one short and close, together covering ≥ 300° around it).
 */
export function textSymbols(sheet, text, wanted = null) {
  const out = new Uint8Array(text.length);
  const SS = sheet.smallShapes || [],
    SK = sheet.smallKind || [];
  const S = sheet.segs,
    F = sheet.segF,
    n = S.length >> 2;
  // grid of short segments (a symbol side is short)
  const CB = 12;
  const g = new Map();
  for (let i = 0; i < n; i++) {
    const x1 = S[i * 4],
      y1 = S[i * 4 + 1],
      x2 = S[i * 4 + 2],
      y2 = S[i * 4 + 3];
    const len = Math.hypot(x2 - x1, y2 - y1);
    if (len < 0.3 || len > 48) continue;
    if (
      F[i] & SEG_FILL &&
      !(F[i] & SEG_STROKE) &&
      sheet.segL &&
      sheet.segL[i] > 238
    )
      continue; // wipeouts
    const k = `${Math.floor((x1 + x2) / 2 / CB)},${Math.floor((y1 + y2) / 2 / CB)}`;
    if (!g.has(k)) g.set(k, []);
    g.get(k).push(i);
  }
  const sg = new Map();
  for (let i = 0; i < SS.length; i += 4) {
    const k = `${Math.floor((SS[i] + SS[i + 2]) / 2 / CB)},${Math.floor((SS[i + 1] + SS[i + 3]) / 2 / CB)}`;
    if (!sg.has(k)) sg.set(k, []);
    sg.get(k).push(i >> 2);
  }
  text.forEach((t, ti) => {
    if (wanted && !wanted(t)) return;
    const cx = (t.x0 + t.x1) / 2,
      cy = (t.y0 + t.y1) / 2;
    const hw = (t.x1 - t.x0) / 2,
      hh = (t.y1 - t.y0) / 2;
    const th = Math.min(hw, hh) * 2 || t.h || 6;
    const R = Math.max(hw, hh) * 2.4 + 2;
    // 1. closed outlines around the text
    let best = null;
    for (let a = Math.floor((cx - R) / CB); a <= Math.floor((cx + R) / CB); a++)
      for (
        let b = Math.floor((cy - R) / CB);
        b <= Math.floor((cy + R) / CB);
        b++
      )
        for (const j of sg.get(`${a},${b}`) || []) {
          const x0 = SS[j * 4],
            y0 = SS[j * 4 + 1],
            x1 = SS[j * 4 + 2],
            y1 = SS[j * 4 + 3];
          if (!(cx > x0 && cx < x1 && cy > y0 && cy < y1)) continue;
          if (x1 - x0 < hw * 2 * 0.9 && y1 - y0 < hh * 2 * 0.9) continue;
          if (Math.max(x1 - x0, y1 - y0) > R * 2.2) continue;
          const ar = (x1 - x0) * (y1 - y0);
          if (!best || ar < best.ar) best = { ar, k: SK[j] || SYM.other };
        }
    if (best && best.k !== SYM.other) {
      out[ti] = best.k;
      return;
    }
    // 2. loose lines ringing the text
    const ring = [];
    for (let a = Math.floor((cx - R) / CB); a <= Math.floor((cx + R) / CB); a++)
      for (
        let b = Math.floor((cy - R) / CB);
        b <= Math.floor((cy + R) / CB);
        b++
      )
        for (const i of g.get(`${a},${b}`) || []) {
          const x1 = S[i * 4],
            y1 = S[i * 4 + 1],
            x2 = S[i * 4 + 2],
            y2 = S[i * 4 + 3];
          const d1 = Math.hypot(x1 - cx, y1 - cy),
            d2 = Math.hypot(x2 - cx, y2 - cy);
          if (d1 > R || d2 > R) continue;
          // closest approach: the outline stays clear of the text itself
          const dx = x2 - x1,
            dy = y2 - y1,
            L2 = dx * dx + dy * dy || 1e-9;
          const u = Math.max(
            0,
            Math.min(1, ((cx - x1) * dx + (cy - y1) * dy) / L2),
          );
          const px = x1 + u * dx - cx,
            py = y1 + u * dy - cy;
          // (inside the text box = a letter's own stroke when text is drawn as lines)
          if (Math.abs(px) < hw * 0.95 && Math.abs(py) < hh * 0.95) continue;
          if (Math.hypot(px, py) < th * 0.45) continue;
          ring.push({
            x1,
            y1,
            x2,
            y2,
            curve: !!(F[i] & SEG_CURVE),
            len: Math.sqrt(L2),
            d: Math.hypot(px, py),
          });
        }
    if (ring.length < 3) return;
    // the outline is the innermost set of lines at about the same distance that goes all the way round
    // (walls, leaders and other symbols nearby sit further out or only on one side)
    ring.sort((a, b) => a.d - b.d);
    let pick = null;
    for (let s0 = 0; s0 < ring.length && !pick; s0++) {
      const d0 = ring[s0].d;
      if (s0 > 0 && d0 < ring[s0 - 1].d * 1.02) continue;
      const set = ring.filter(
        (r) => r.d >= d0 * 0.97 && r.d <= d0 * 1.45 + 0.4,
      );
      if (set.length >= 3 && coverage(set, cx, cy) >= (300 * Math.PI) / 180)
        pick = set;
    }
    if (!pick) return;
    ring.length = 0;
    ring.push(...pick);
    // shape from the ring's directions: which family of side directions alone still goes all the way round
    let tot = 0,
      curveLen = 0;
    let x0 = Infinity,
      y0 = Infinity,
      x1 = -Infinity,
      y1 = -Infinity;
    for (const s of ring) {
      tot += s.len;
      if (s.curve) curveLen += s.len;
      x0 = Math.min(x0, s.x1, s.x2);
      x1 = Math.max(x1, s.x1, s.x2);
      y0 = Math.min(y0, s.y1, s.y2);
      y1 = Math.max(y1, s.y1, s.y2);
    }
    const w = x1 - x0,
      h = y1 - y0;
    if (curveLen > tot * 0.5) {
      out[ti] = Math.min(w, h) >= Math.max(w, h) * 0.8 ? SYM.circle : SYM.oval;
      return;
    }
    const dirOf = (s) => {
      let a = (Math.atan2(s.y2 - s.y1, s.x2 - s.x1) * 180) / Math.PI;
      return ((a % 180) + 180) % 180;
    };
    const near = (a, d) =>
      Math.min(Math.abs(a - d), 180 - Math.abs(a - d)) <= 9;
    const fam = (dirs) =>
      ring.filter((s) => !s.curve && dirs.some((d) => near(dirOf(s), d)));
    const full = (set, deg = 290) =>
      set.length >= 3 && coverage(set, cx, cy) >= (deg * Math.PI) / 180;
    // sides: one per direction and side of the centre
    const sides = (set) => {
      const k = new Set();
      for (const s of set) {
        const a = Math.round(dirOf(s) / 15) % 12;
        const nx = -(s.y2 - s.y1),
          ny = s.x2 - s.x1;
        const sd = Math.sign(
          nx * ((s.x1 + s.x2) / 2 - cx) + ny * ((s.y1 + s.y2) / 2 - cy),
        );
        k.add(`${a}|${sd}`);
      }
      return k.size;
    };
    const dia = fam([45, 135]),
      box = fam([0, 90]),
      hexA = fam([0, 60, 120]),
      hexB = fam([90, 30, 150]);
    if (full(dia)) out[ti] = SYM.diamond;
    else if (full(hexA) || full(hexB)) {
      const set = full(hexA) ? hexA : hexB;
      out[ti] = sides(set) >= 5 ? SYM.hexagon : SYM.triangle;
    } else if (full(box))
      out[ti] = curveLen > tot * 0.15 ? SYM.oval : SYM.square;
    else if (curveLen > tot * 0.2) out[ti] = SYM.oval;
    else if (full(fam([0, 45, 90, 135]))) out[ti] = SYM.octagon;
    else out[ti] = SYM.other;
  });
  return out;
}

// angle (radians) the segments cover, seen from (cx, cy)
function coverage(segs, cx, cy) {
  const norm = [];
  for (const s of segs) {
    const a1 = Math.atan2(s.y1 - cy, s.x1 - cx),
      a2 = Math.atan2(s.y2 - cy, s.x2 - cx);
    let d = a2 - a1;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    let lo = d >= 0 ? a1 : a1 + d;
    while (lo < 0) lo += 2 * Math.PI;
    while (lo >= 2 * Math.PI) lo -= 2 * Math.PI;
    const hi = lo + Math.abs(d);
    if (hi > 2 * Math.PI) {
      norm.push([lo, 2 * Math.PI]);
      norm.push([0, hi - 2 * Math.PI]);
    } else norm.push([lo, hi]);
  }
  norm.sort((p, q) => p[0] - q[0]);
  let cov = 0,
    cur = null;
  for (const [a, b] of norm) {
    if (!cur || a > cur[1] + 0.03) {
      if (cur) cov += cur[1] - cur[0];
      cur = [a, b];
    } else cur[1] = Math.max(cur[1], b);
  }
  if (cur) cov += cur[1] - cur[0];
  return cov;
}

// ───────────────────────── column grid bubbles ─────────────────────────
const GRID_LABEL =
  /^(?:[A-Z]{1,2}|[A-Z]?\d{1,3}(?:\.\d{1,2})?[A-Z]?|[A-Z]\.\d{1,2}|[A-Z]'{1,2}|\d{1,2}')$/;
/** grid bubbles: a circle (or hexagon) with one short label — [{x, y, r, label}] */
export function gridBubbles(text, sym) {
  const out = [];
  text.forEach((t, i) => {
    const k = sym[i];
    if (k !== SYM.circle && k !== SYM.hexagon && k !== SYM.octagon) return;
    const s = t.str.replace(/\s+/g, "").toUpperCase();
    if (!GRID_LABEL.test(s) || /^[A-Z]\d\.\d{2}$/.test(s)) return; // (A6.10 = a sheet reference in a callout)
    const cx = (t.x0 + t.x1) / 2,
      cy = (t.y0 + t.y1) / 2;
    const r = Math.max(t.x1 - t.x0, t.y1 - t.y0) * 0.9 + 2;
    out.push({
      x: cx,
      y: cy,
      r,
      label: s,
      h: Math.min(t.x1 - t.x0, t.y1 - t.y0),
    });
  });
  // a callout bubble (section / detail mark) holds two labels or a sheet number — grid bubbles are alone
  const alone = out.filter(
    (b) =>
      !out.some((o) => o !== b && Math.hypot(o.x - b.x, o.y - b.y) < b.r * 1.1),
  );
  // a grid label shows at the ends of its line (2–4 times); a door tag like "D1" repeats all over the plan
  const cnt = new Map();
  for (const b of alone) cnt.set(b.label, (cnt.get(b.label) || 0) + 1);
  const kept = alone.filter((b) => cnt.get(b.label) <= 4);
  return new Set(kept.map((b) => b.label)).size >= 3 ? kept : [];
}

// ───────────────────────── colour legend ─────────────────────────
/**
 * Legend lines: a short coloured sample (a line, a dashed line, a box) with words right of it —
 * "2 HOUR FIRE RATING", "0 HOUR FIRE RATING", "SMOKE SEPARATION" … → [{color, hex, family, label}]
 */
export function colorLegend(sheet) {
  const S = sheet.segs,
    C = sheet.segC,
    n = S.length >> 2;
  if (!C || !C.length) return [];
  const text = sheet.text || [];
  // coloured pieces grouped into samples (close together, one colour family)
  const CB = 6,
    g = new Map(),
    pieces = [];
  for (let i = 0; i < n; i++) {
    const c = C[i];
    if (!isColored(c)) continue;
    const x1 = S[i * 4],
      y1 = S[i * 4 + 1],
      x2 = S[i * 4 + 2],
      y2 = S[i * 4 + 3];
    const fam = colorFamily(c);
    const id = pieces.length;
    pieces.push({
      x0: Math.min(x1, x2),
      y0: Math.min(y1, y2),
      x1: Math.max(x1, x2),
      y1: Math.max(y1, y2),
      c,
      fam,
    });
    const k = `${Math.floor((x1 + x2) / 2 / CB)},${Math.floor((y1 + y2) / 2 / CB)}`;
    if (!g.has(k)) g.set(k, []);
    g.get(k).push(id);
  }
  if (!pieces.length) return [];
  const p = pieces.map((_, i) => i);
  const f = (x) => {
    while (p[x] !== x) x = p[x] = p[p[x]];
    return x;
  };
  for (const [k, ids] of g) {
    const [a, b] = k.split(",").map(Number);
    for (let da = -1; da <= 1; da++)
      for (let db = -1; db <= 1; db++)
        for (const j of g.get(`${a + da},${b + db}`) || [])
          for (const i of ids) {
            if (i >= j) continue;
            const P = pieces[i],
              Q = pieces[j];
            if (P.fam !== Q.fam) continue;
            if (
              P.x0 - Q.x1 > 4 ||
              Q.x0 - P.x1 > 4 ||
              P.y0 - Q.y1 > 4 ||
              Q.y0 - P.y1 > 4
            )
              continue;
            p[f(j)] = f(i);
          }
  }
  const groups = new Map();
  pieces.forEach((q, i) => {
    const r = f(i);
    const e = groups.get(r) || {
      x0: Infinity,
      y0: Infinity,
      x1: -Infinity,
      y1: -Infinity,
      cnt: new Map(),
      fam: q.fam,
    };
    e.x0 = Math.min(e.x0, q.x0);
    e.y0 = Math.min(e.y0, q.y0);
    e.x1 = Math.max(e.x1, q.x1);
    e.y1 = Math.max(e.y1, q.y1);
    e.cnt.set(q.c, (e.cnt.get(q.c) || 0) + 1);
    groups.set(r, e);
  });
  const found = new Map(); // family → best entry
  for (const e of groups.values()) {
    const w = e.x1 - e.x0,
      h = e.y1 - e.y0;
    // a legend sample is small and wider than tall (a line or a short band) — walls are long
    if (w < 8 || w > 160 || h > 16) continue;
    const my = (e.y0 + e.y1) / 2;
    // words starting just right of the sample, on the same line
    const row = text
      .filter(
        (t) =>
          t.x0 >= e.x1 - 2 &&
          t.x0 <= e.x1 + 60 &&
          t.y0 <= my + 6 &&
          t.y1 >= my - 6 &&
          t.str.trim().length > 1,
      )
      .sort((a, b) => a.x0 - b.x0);
    if (!row.length) continue;
    // the line of text: items joined until a big gap
    const parts = [row[0]];
    for (const t of row.slice(1)) {
      const last = parts[parts.length - 1];
      if (t.x0 - last.x1 > Math.max(12, (last.y1 - last.y0) * 2)) break;
      parts.push(t);
    }
    const label = parts
      .map((t) => t.str.trim())
      .join(" ")
      .replace(/\s+/g, " ")
      .slice(0, 60);
    if (!/[A-Z]{3,}/i.test(label)) continue;
    const color = [...e.cnt].sort((a, b) => b[1] - a[1])[0][0];
    const score =
      /RAT|HOUR|HR\b|FIRE|SMOKE|SOUND|STC|WALL|PARTITION|SEPARATION|BARRIER|DEMISING/i.test(
        label,
      )
        ? 2
        : 1;
    const prev = found.get(e.fam);
    if (!prev || score > prev.score)
      found.set(e.fam, {
        color,
        hex: hexOf(color),
        family: e.fam,
        label,
        score,
      });
  }
  return [...found.values()].map(({ score, ...r }) => r);
}
